<template>
  <div class="footer-newsletter">
    <form
      novalidate
      class="relative between-md middle-md"
      @submit.prevent="checkSubscription"
    >
      <div class="flex flex-column w-100">
        <div>
          <cms-block
            class="footer-newsletter-block"
            :identifier="'keep-up-to-date'"
          />
        </div>
        <div class="footer-newsletter-content mt24 mb40 w-100 flex">
          <div class="footer-newsletter-content-input center-self flex w-100 px0-desktop">
            <base-input
              v-model="email"
              class="w-100 mr12"
              type="email"
              name="email"
              :placeholder="$t('Enter your email here')"
              :validations="[
                {
                  condition: $v.email.$error && !$v.email.required,
                  text: $t('Field is required.')
                },
                {
                  condition: !$v.email.email && $v.email.$error,
                  text: $t('Please provide valid e-mail address.')
                }
              ]"
            />
            <button
              class="button row"
              type="submit"
              @click="$v.email.$touch()"
            >
              <img
                src="/assets/icons/bell-white.svg"
              >
              <span>
                {{ $t('Subscribe') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import SubscriptionStatus from 'theme/mixins/subscriptionStatus'
import Subscribe from '@vue-storefront/core/modules/newsletter/mixins/Subscribe'
import i18n from '@vue-storefront/i18n'
import { Logger } from '@vue-storefront/core/lib/logger'

import BaseInput from 'theme/components/core/blocks/Form/BaseInput'
import CmsBlock from 'theme/components/core/blocks/Cms/Block'

export default {
  components: {
    BaseInput,
    CmsBlock
  },
  mixins: [
    SubscriptionStatus,
    Subscribe
  ],
  props: {
    artistName: {
      type: String,
      default: ''
    },
    blockIdentifier: {
      type: String,
      default: ''
    }
  },
  computed: {
    artistNameHeading () {
      if (this.artistName) {
        return i18n.t('Be the first to know when there is a new event with {artistName}.',
          { artistName: this.artistName })
      } else {
        return null
      }
    },
    artistNameContent () {
      if (this.artistName) {
        return i18n.t('Please leave your email in order to receive a notification as soon as an event with {artistName} is available.',
          { artistName: this.artistName })
      } else {
        return null
      }
    }
  },
  beforeDestroy () {
    this.$off('validation-error')
  },
  methods: {
    onSuccessfulSubmission (isSubscribed) {
      if (isSubscribed) {
        this.$store.dispatch('klaviyo/updateCustomerEmail', { email: this.email })
        this.$store.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('You have been successfully subscribed to our newsletter!'),
          action1: { label: i18n.t('OK') }
        })
      }
      this.email = ''
      this.$v.email.$reset()
    },
    onFailureSubmission (error) {
      Logger.error(error)
    },
    async checkSubscription () {
      if (!this.$v.email.$error) {
        if (!this.$store.state.user.current) await this.$store.dispatch('newsletter/status', this.email)
        if (!this.isSubscribed) {
          await this.subscribe(this.onSuccessfulSubmission, this.onFailureSubmission)
        } else {
          await this.$store.dispatch('notification/spawnNotification', {
            type: 'success',
            message: i18n.t('You previously have been subscribed to our newsletter!'),
            action1: { label: i18n.t('OK') }
          })
        }
      }
    }
  },
  validations: {
    email: {
      required,
      email
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';

.footer-newsletter {
  &-block {
    /deep/ * {
      color: #fff;
    }

    /deep/ h4 {
      font-size: 24px;
      font-weight: 700;
    }

    /deep/ p {
      font-size: 22px;
      font-weight: 400;
      margin-bottom: 0;
    }
  }

  &-heading {
    h2 {
      font-size: 18px;
      line-height: 24px;
    }
  }

  &-content {
    &-input {
      height: 56px;
      border-radius: 5px;

      .base-input {
        margin: 0;
        margin-right: 12px;

        /deep/ .form-input {
          border: none;
          background-color: color(whitesmoke);
          border-radius: 10px;

          &.error {
            border: 1px solid color(red);
          }

          &.empty {
            background-color: color(whitesmoke);
          }

          &::placeholder {
            color: color(input-newsletter-placeholder);
          }

          &:focus {
            &::placeholder {
              color: color(transparent);
            }
          }
        }
      }

      .button {
        display: flex;
        flex-wrap: nowrap;
        margin: 0;
        padding: 18px 22px;
        border-radius: 10px;
        font-size: 18px;
        background-color: color(primary);
        height: 100%;
        flex-direction: row;

        @include desktop {
          &:hover {
            background-color: color(primary-hover);
          }
        }

        &:focus-visible {
          outline: 2px solid color(white);
          outline-offset: 2px;
        }

        span {
          color: #fff;
          margin-left: 10px;
          line-height: 20px;
        }
      }
    }
  }

  /deep/ {
    .validation-text {
      margin-top: -5px;
      margin-left: 2px;
    }
  }
}

@include mobile {
  .footer-newsletter {
    &-block {
      /deep/ h4 {
        font-size: 18px;
        font-weight: 700;
        line-height: 22px;
      }

      /deep/ p {
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
      }
    }

    &-content {
      margin-top: 20px;

      &-input {
        height: 50px;

        /deep/ .form-input {
          height: 50px;
        }

        .button {
          padding: 14px;

          img {
            width: 22px;
            height: 22px;
          }

          span {
            display: none;
          }
        }
      }
    }
  }
}

@include desktop {
  .footer-newsletter {
    form {
      display: flex;
      flex-direction: row;

      .footer-newsletter-heading {
        h2 {
          font-size: 25px;
          line-height: 30px;
        }
      }

      .footer-newsletter-content {
        flex-direction: row;

        span {
          line-height: 21px;
          max-width: 348px;
        }
      }
    }
  }
}
</style>
